import styled from '@emotion/styled';
import { useParentSize } from '@visx/responsive';
import { AnimatePresence } from 'framer-motion';
import { useState, useEffect, useMemo } from 'react';
import { useDrop } from 'react-dnd';
import { useLocation } from 'react-router-dom';

import { Layout } from '../../Layout';
import { RouteManager } from '../../RouteManager';

import { LogUtils } from '../../store/insights/LogUtils.js';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { useQuery } from '../../hooks/useQuery';
import { useDevicesAttach } from '../../store/devices/useDevices.ts';
import { useLogsById } from '../../store/insights/useLogs.ts';
import { useLogsAttach } from '../../store/insights/useLogs.ts';
import { useApi } from '../../store/SelectedHomeyStore.hooks.ts';
import { useZonesAttach } from '../../store/zones/useZones.ts';

import { su } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';

import { MonoButtonRound } from '../../components/buttons/MonoButtonRound';
import { CenteredMessage } from '../../components/common/CenteredMessage';
import { Glyph } from '../../components/common/Glyph';
import { Scroll } from '../../components/common/Scroll';
import { dragTypes } from '../../components/dnd/dragTypes';
import { PremiumRequiredDialog } from '../../components/premium/PremiumRequiredDialog';

import { InsightListBox } from './insight-listbox/InsightListBox';
import { LogDropZone } from './LogDropZone';
import { LogGroup } from './LogGroup';
import { ResolutionSelect } from './resolution-select/ResolutionSelect';

export function InsightsPage() {
  const { i18n } = useI18n();

  const location = useLocation();
  const query = useQuery();

  const { api } = useApi();

  useDevicesAttach();
  useZonesAttach();
  useLogsAttach();

  const { parentRef: gridRef, width: gridWidth } = useParentSize({
    ignoreDimensions: ['height'],
    debounceTime: 0,
    enableDebounceLeadingCall: true,
  });

  const isPremiumLocked =
    api?.tier !== 'premium' && api?.homey.platform !== undefined && api?.homey.platform !== 'local';
  const logs = useLogsById();

  const [selectedResolutionKey, setSelectedResolutionKey] = useLocalStorageState(
    'last7Days',
    'insightsResolutionSelect',
  );

  const [chipDeviceId, setChipDeviceId] = useState(null);

  function getNextGroupKey(currentGroups) {
    let index = null;
    let cursor = 1;

    while (index === null) {
      if (currentGroups[cursor] == null) {
        index = cursor;
      } else {
        cursor++;
      }
    }

    return index;
  }

  function getSelectionUrl(groups) {
    let url = '';

    const groupEntries = Object.entries(groups);

    for (const [index, [logGroupKey, logKeys]] of groupEntries.entries()) {
      let postfix = '';

      if (index !== groupEntries.length - 1) {
        postfix = '&';
      }

      url += `logs${logGroupKey}=${logKeys.join(',')}${postfix}`;
    }

    return url;
  }

  useEffect(() => {
    if (location.state?.defaultSelectedKeys != null && logs.byId != null) {
      if (isPremiumLocked === true) {
        RouteManager.toPremiumRequiredDialog();
        return;
      }
      const nextLogsGroups = {};
      location.state.defaultSelectedKeys.forEach((key) => {
        const log = logs.byId[key];

        if (log != null) {
          nextLogsGroups[`${getNextGroupKey(nextLogsGroups)}`] = [key];
        }
      });

      const nextRouteState = {
        ...location.state,
      };
      delete nextRouteState.defaultSelectedKeys;
      delete nextRouteState.deviceId;

      if (location.state.deviceId) {
        setChipDeviceId(location.state.deviceId);
      }

      RouteManager.toInsights(nextRouteState, getSelectionUrl(nextLogsGroups), { replace: true });
    }
  }, [location.state, logs.byId, isPremiumLocked]);

  const { logGroups, logGroupEntries, selectedLogKeys } = useMemo(() => {
    const logGroups = {};
    const selectedLogKeys = new Set();

    for (const [key, value] of query.entries()) {
      if (key.startsWith('logs')) {
        const groupKey = key.replace('logs', '');
        logGroups[groupKey] = value.split(',');
        for (const logKey of logGroups[groupKey]) {
          selectedLogKeys.add(logKey);
        }
      }
    }

    return { logGroups, logGroupEntries: Object.entries(logGroups), selectedLogKeys };
  }, [query]);

  // non merging dropzone
  const [{ isOver, canDrop }, dropRef] = useDrop({
    accept: [dragTypes.LOG],
    collect: (dropMonitor) => {
      return {
        isOver: dropMonitor.isOver({ shallow: true }),
        canDrop: dropMonitor.canDrop(),
      };
    },
    canDrop(dragItem, dropMonitor) {
      return true;
    },
    hover(item, monitor) {},
    drop(dragItem, monitor) {
      if (monitor.didDrop()) return;

      onDrop({
        targetGroupKey: null,
        logKey: dragItem.id,
        units: dragItem.units,
      });
    },
  });

  function onDrop({ targetGroupKey, logKey, units }) {
    if (isPremiumLocked === true) {
      RouteManager.toPremiumRequiredDialog();
      return;
    }

    const group = Object.entries(logGroups).find(([groupKey]) => {
      return groupKey === targetGroupKey;
    });

    let nextLogGroups;

    if (group) {
      const [groupKey, groupValues] = group;

      // already part of this group
      if (groupValues.includes(logKey)) return logGroups;

      nextLogGroups = {
        ...logGroups,
        [groupKey]: [...groupValues, logKey],
      };
    } else {
      nextLogGroups = {
        ...logGroups,
        [`${getNextGroupKey(logGroups)}`]: [logKey],
      };
    }

    RouteManager.toInsights({}, getSelectionUrl(nextLogGroups));
  }

  function handleSelectionChange(selectedKeys) {
    if (isPremiumLocked === true) {
      RouteManager.toPremiumRequiredDialog();
      return;
    }

    const removed = new Set([...selectedLogKeys].filter((x) => !selectedKeys.has(x)));
    const added = new Set([...selectedKeys].filter((x) => !selectedLogKeys.has(x)));

    let nextLogGroups = { ...logGroups };

    [...removed].forEach((removedValue) => {
      nextLogGroups = Object.entries(nextLogGroups).reduce(
        (accumulator, [groupKey, groupValues]) => {
          let nextGroupValues = groupValues;

          if (groupValues.includes(removedValue)) {
            nextGroupValues = groupValues.filter((value) => value !== removedValue);
          }

          if (nextGroupValues.length === 0) {
            return accumulator;
          }

          accumulator[groupKey] = nextGroupValues;
          return accumulator;
        },
        {},
      );
    });

    [...added].forEach((logKey) => {
      nextLogGroups[`${getNextGroupKey(nextLogGroups)}`] = [logKey];
    });

    RouteManager.toInsights({}, getSelectionUrl(nextLogGroups));
  }

  function removeKeyFromGroup(groupKey, logKey) {
    const nextInsightGroups = { ...logGroups };

    nextInsightGroups[groupKey] = nextInsightGroups[groupKey].filter((key) => {
      return logKey !== key;
    });

    if (nextInsightGroups[groupKey].length === 0) {
      delete nextInsightGroups[groupKey];
    }

    const occursInOtherGroup = Object.values(nextInsightGroups).some((group) => {
      return group.includes(logKey);
    });

    if (occursInOtherGroup) {
    } else {
      const nextSelectionInsightKeys = new Set(selectedLogKeys);
      nextSelectionInsightKeys.delete(logKey);
    }

    RouteManager.toInsights({}, getSelectionUrl(nextInsightGroups));
  }

  function removeGroup(groupKey) {
    const nextInsightGroups = { ...logGroups };

    if (nextInsightGroups[groupKey]) {
      const currentGroup = nextInsightGroups[groupKey];
      delete nextInsightGroups[groupKey];

      const nextSelectionInsightKeys = new Set(selectedLogKeys);
      currentGroup.forEach((logKey) => {
        let occursInOtherGroup = Object.values(nextInsightGroups).some((group) => {
          return group.includes(logKey);
        });

        if (!occursInOtherGroup) {
          nextSelectionInsightKeys.delete(logKey);
        }
      });

      RouteManager.toInsights({}, getSelectionUrl(nextInsightGroups));
    }
  }

  return (
    <Layout
      actions={
        <InsightsPage.Actions>
          <ResolutionSelect
            selectedKey={selectedResolutionKey}
            onSelectionChange={setSelectedResolutionKey}
          />

          {selectedLogKeys.size > 0 && (
            <MonoButtonRound
              onPress={() => {
                handleSelectionChange(new Set());
              }}
            >
              <Glyph
                source={glyph.core.cross}
                color={theme.color.text}
                size={theme.icon.size_tiny}
              />
              <MonoButtonRound.Text>
                {i18n.messageFormatter('insights.closeAll')}
              </MonoButtonRound.Text>
            </MonoButtonRound>
          )}
        </InsightsPage.Actions>
      }
      subNavigationProps={{
        key: 'insights',
        title: i18n.messageFormatter('navigation.insights'),
        width: 280,
      }}
      subNavigationChildren={
        !logs.isLoading && (
          <InsightListBox
            selectedKeys={selectedLogKeys}
            selectedResolutionKey={selectedResolutionKey}
            chipDeviceId={chipDeviceId}
            setChipDeviceId={setChipDeviceId}
            onSelectionChange={handleSelectionChange}
          />
        )
      }
      content={
        logGroupEntries.length === 0 &&
        canDrop === false &&
        location.state?.defaultSelectedKeys == null ? (
          <Scroll flex>
            <InsightsPage.MessageWrapper>
              <CenteredMessage
                iconUrl={glyph.system.insights}
                iconColor={theme.color.text_light}
                title={i18n.messageFormatter('insights.emptySelectionTitle')}
                titleColor={theme.color.text_light}
                subtitle={i18n.messageFormatter('insights.emptySelectionSubtitle')}
                subtitleColor={theme.color.text_light}
              />
            </InsightsPage.MessageWrapper>
          </Scroll>
        ) : (
          <Scroll>
            <InsightsPage.Grid.Root ref={gridRef}>
              {logGroupEntries.map(([logGroupKey, logKeys]) => {
                const log = logs.byId?.[logKeys[0]];
                let units = LogUtils.getUnits(log);

                // Cast null back to its proper value.
                if (units === 'null') {
                  units = null;
                }

                return (
                  <InsightsPage.Grid.Item key={logGroupKey}>
                    <LogGroup
                      key={logGroupKey}
                      groupKey={logGroupKey}
                      groupUnits={units}
                      logKeys={logKeys}
                      width={gridWidth}
                      resolutionId={selectedResolutionKey}
                      onDrop={onDrop}
                      onRequestSelectKeys={(keys) => {
                        const nextLogGroups = { ...logGroups };
                        nextLogGroups[logGroupKey] = [...nextLogGroups[logGroupKey], ...keys];
                        RouteManager.toInsights({}, getSelectionUrl(nextLogGroups));
                      }}
                      onRemoveIdRequest={({ logId }) => removeKeyFromGroup(logGroupKey, logId)}
                      onRequestRemoveGroup={() => removeGroup(logGroupKey)}
                    />
                  </InsightsPage.Grid.Item>
                );
              })}

              {canDrop && (
                <InsightsPage.Grid.Item>
                  <LogDropZone
                    dropRef={dropRef}
                    isOver={isOver}
                    canDrop={canDrop}
                    height={280}
                    width={gridWidth}
                  />
                </InsightsPage.Grid.Item>
              )}
            </InsightsPage.Grid.Root>
          </Scroll>
        )
      }
    >
      <AnimatePresence>
        {location.state?.premiumRequiredDialog === true && <PremiumRequiredDialog />}
      </AnimatePresence>
    </Layout>
  );
}

InsightsPage.Actions = styled.div`
  display: flex;
  padding-inline-start: ${su(4)};

  > :not(:last-child) {
    margin-inline-end: ${su(2)};
  }
`;

InsightsPage.MessageWrapper = styled.section`
  display: flex;
  flex: 1 1 auto;
  position: relative;
`;

InsightsPage.Grid = {};
InsightsPage.Grid.Root = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  padding: ${su(2, 6, 2, 4)};
`;
InsightsPage.Grid.Item = styled.section`
  margin: ${su(4)} 0;

  &:first-of-type {
    margin-top: 0;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export default InsightsPage;
